import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import { storageURL, mediaUrl } from '@config'
import publicApiClient from '@services/api/publicClient'
import likesCommentsService from '@services/api/likesComments'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import { encodeId, decodeId } from '@utils/idCipher'

function VideoDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel pour les appels API
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [video, setVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [relatedVideos, setRelatedVideos] = useState([])
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [activeTab, setActiveTab] = useState('comments')
  const [isLiked, setIsLiked] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [subscribersCount, setSubscribersCount] = useState(0)
  const [error, setError] = useState(null)
  const [loadingComment, setLoadingComment] = useState(false)
  const incrementedIdRef = useRef(null)

  // Récupérer les données de la vidéo depuis l'API
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true)
        setError(null)

        const res = await publicApiClient.get(`/posts/${id}`)
        const p = res.data || res

        if (!p || !p.post_id) {
          setError('Vidéo non trouvée')
          setLoading(false)
          return
        }

        // Trouver le fichier vidéo et la miniature dans fichiers[]
        const videoFile = p.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
        const thumbFile = p.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))

        const clientPhoto = p.client?.photo_profil
        const avatarUrl = clientPhoto
          ? (clientPhoto.startsWith('http') ? clientPhoto : mediaUrl(clientPhoto))
          : null

        const videoData = {
          id:          p.post_id,
          title:       p.libele,
          description: p.description || 'Aucune description disponible',
          videoUrl:    videoFile ? mediaUrl(videoFile.chemin) : null,
          thumbnail:   thumbFile ? mediaUrl(thumbFile.chemin) : null,
          views:       p.vues || 0,
          likes:       p.post_likes_count || 0,
          dislikes: 0,
          comments: p.post_comments_count ?? 0,
          shares:   0,
          uploadDate:  p.created_at,
          category:    p.category?.libele_categorie || 'Non classé',
          tags:        [],
          clientId:    p.post_client_id,
          author: {
            id:       p.client?.client_id || 0,
            name:     [p.client?.nom, p.client?.prenom].filter(Boolean).join(' ') || 'Utilisateur',
            avatar:   avatarUrl,
            subscribers: 0,
            verified: false
          },
          product: {
            id:    p.post_id,
            name:  p.libele,
            price: p.prix,
          }
        }

        setVideo(videoData)

        // Vérifier si l'utilisateur a liké (connexion requise)
        if (user?.client_id || user?.id) {
          try {
            const likeStatus = await likesCommentsService.checkLike(id, user?.id || user?.client_id)
            setIsLiked(likeStatus.isLiked || false)
          } catch (err) {
            console.error('Erreur lors de la vérification du like:', err)
          }
        }

        // Récupérer les commentaires (stocker bruts comme ProductDetailPage)
        try {
          const commentsData = await likesCommentsService.getComments(id)
          if (commentsData.success) {
            const list = commentsData.comments || []
            setComments(list)
            setVideo(prev => prev ? { ...prev, comments: list.length } : prev)
          }
        } catch (err) {
          console.error('Erreur lors du chargement des commentaires:', err)
        }

        // Récupérer les vidéos similaires
        try {
          const relatedData = await publicApiClient.get('/posts', {
            params: { post_categorie_id: p.post_categorie_id, type: 1, status: 2 }
          })
          const items = relatedData.data || relatedData.posts || (Array.isArray(relatedData) ? relatedData : [])
          const formattedRelated = items
            .filter(post => post.post_id !== parseInt(id))
            .slice(0, 8)
            .map(post => ({
              id: post.post_id || post.id,
              title: post.libele || post.name,
              thumbnail: post.fichiers?.[0]?.chemin ? mediaUrl(post.fichiers[0].chemin) : null,
              author: post.client_name || [post.client?.prenom, post.client?.nom].filter(Boolean).join(' ') || 'Utilisateur',
              views: post.vues || 0,
              duration: '0:00',
              uploadDate: formatDate(post.created_at)
            }))
          setRelatedVideos(formattedRelated)
        } catch (err) {
          console.error('Erreur lors du chargement des vidéos similaires:', err)
        }

        // Abonnement — lecture depuis localStorage (comme ProductDetailPage)
        const currentUserId = user?.id || user?.client_id
        if (currentUserId && videoData.author?.id) {
          const subKey = `sub_${currentUserId}_${videoData.author.id}`
          setIsSubscribed(localStorage.getItem(subKey) === 'true')
        }
        setSubscribersCount(videoData.author?.subscribers || 0)

      } catch (err) {
        console.error('Erreur lors du chargement de la vidéo:', err)
        setError('Erreur lors du chargement de la vidéo')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchVideo()
    }
  }, [id])

  // Incrément de vue — guard contre double-appel StrictMode
  useEffect(() => {
    if (!id || incrementedIdRef.current === id) return
    incrementedIdRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Aujourd\'hui'
    if (diffDays === 1) return 'Hier'
    if (diffDays < 7) return `Il y a ${diffDays} jours`
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`
    return `Il y a ${Math.floor(diffDays / 365)} ans`
  }

  const handleLike = async () => {
    const clientId = user?.id || user?.client_id
    if (!clientId) {
      alert('Vous devez être connecté pour liker cette vidéo')
      return
    }
    try {
      if (isLiked) {
        await likesCommentsService.unlikePost(id, clientId)
        setIsLiked(false)
        setVideo(prev => ({ ...prev, likes: Math.max(0, prev.likes - 1) }))
      } else {
        await likesCommentsService.likePost(id, clientId)
        setIsLiked(true)
        setVideo(prev => ({ ...prev, likes: prev.likes + 1 }))
      }
    } catch (err) {
      console.error('Erreur lors du like:', err)
    }
  }

  const handleDislike = () => {
    // Fonctionnalité dislike à implémenter si besoin
    console.log('Disliked')
  }

  const handleShare = () => {
    setShowShareModal(true)
  }

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return }
    const clientId = user?.id || user?.client_id
    const abonneId = video?.author?.id
    if (!abonneId) return
    if (Number(clientId) === Number(abonneId)) return
    try {
      await apiClient.post('/subscription/subscribe', {
        action: 'toggle',
        client_id: clientId,
        abonne_id: abonneId
      })
      const newState = !isSubscribed
      setIsSubscribed(newState)
      setSubscribersCount(prev => newState ? prev + 1 : Math.max(0, prev - 1))
      localStorage.setItem(`sub_${clientId}_${abonneId}`, newState)
      toast.success(newState ? 'Abonné avec succès' : 'Désabonné avec succès')
    } catch (err) {
      toast.error('Erreur lors de l\'abonnement')
    }
  }

  const handleCommentSubmit = async (e) => {
    e.preventDefault()
    if (!newComment.trim()) return
    if (!isAuthenticated) { navigate('/login'); return }
    const currentUserId = user?.id || user?.client_id
    setLoadingComment(true)
    try {
      const response = await likesCommentsService.addComment(id, newComment, currentUserId)
      if (response.success) {
        setComments(prev => [response.comment, ...prev])
        setNewComment('')
        setVideo(prev => prev ? { ...prev, comments: (prev.comments || 0) + 1 } : prev)
      }
    } catch (err) {
      console.error('Erreur lors de l\'ajout du commentaire:', err)
    } finally {
      setLoadingComment(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la vidéo...</p>
        </div>
      </div>
    )
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Vidéo non trouvée</h2>
          <p className="text-gray-600 mb-4">{error || 'Cette vidéo n\'existe pas ou a été supprimée'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full lg:w-[90%] xl:w-[85%] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 md:p-6">
          {/* Colonne principale - Vidéo */}
          <div className="lg:col-span-2 space-y-4">
            {/* Lecteur vidéo ou image */}
            <div className="bg-black rounded-xl overflow-hidden aspect-video relative group">
              {video.videoUrl && (video.videoUrl.includes('.mp4') || video.videoUrl.includes('.webm') || video.videoUrl.includes('video')) ? (
                <>
                  <video
                    className="w-full h-full"
                    controls
                    preload="metadata"
                    poster={video.thumbnail}
                    src={video.videoUrl}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  >
                    <source src={video.videoUrl} type="video/mp4" />
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                  
                  {/* Overlay avec info */}
                  {!isPlaying && (
                    <div 
                      className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-center justify-center opacity-100 group-hover:opacity-100 transition-opacity pointer-events-none"
                    >
                      <button 
                        className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center hover:bg-[#c09d2a] transition-all hover:scale-110 pointer-events-auto"
                        onClick={(e) => {
                          e.preventDefault()
                          const video = e.target.closest('div').previousElementSibling
                          if (video && video.tagName === 'VIDEO') {
                            video.play()
                          }
                        }}
                      >
                        <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <img 
                  src={video.thumbnail} 
                  alt={video.title}
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Titre et stats */}
            <div className="bg-white rounded-xl p-4 md:p-6">
              <h1 className="text-xl md:text-2xl font-bold text-[#001F3F] mb-3">
                {video.title}
              </h1>

              {/* Infos et actions */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span>{video.views.toLocaleString()} vues</span>
                  </div>
                  <span>•</span>
                  <span>{new Date(video.uploadDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                      isLiked 
                        ? 'bg-[#D4AF37] text-white hover:bg-[#c09d2a]' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    <svg className="w-5 h-5" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                    <span className="text-sm font-medium">{video.likes}</span>
                  </button>

                  <button
                    onClick={handleDislike}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                  </button>

                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="text-sm font-medium hidden sm:inline">Partager</span>
                  </button>
                </div>
              </div>

              {/* Auteur */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t pt-4">
                <div className="flex items-center gap-3">
                  {video.author.avatar ? (
                    <img
                      src={video.author.avatar}
                      alt={video.author.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] items-center justify-center text-white font-bold text-lg flex-shrink-0"
                    style={{ display: video.author.avatar ? 'none' : 'flex' }}
                  >
                    {video.author.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#001F3F]">{video.author.name}</h3>
                    <p className="text-sm text-gray-600">{subscribersCount.toLocaleString()} abonné{subscribersCount > 1 ? 's' : ''}</p>
                  </div>
                </div>
                {Number(user?.id || user?.client_id) !== Number(video.author.id) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => {
                        if (!isAuthenticated) { navigate('/login'); return }
                        navigate('/messages', {
                          state: {
                            openConversationWith: {
                              id:     video.author?.id,
                              name:   video.author?.name,
                              avatar: video.author?.avatar,
                              article: {
                                name:  video.title,
                                url:   `/video/${encodedId}`,
                                image: video.thumbnail,
                              }
                            }
                          }
                        })
                      }}
                      className="bg-[#D4AF37] text-white p-2 rounded-lg hover:bg-[#c09d2a] transition-colors"
                      title="Message"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={handleSubscribe}
                      className={`px-6 py-2 rounded-full font-medium transition-colors ${
                        isSubscribed
                          ? 'bg-gray-400 text-white hover:bg-gray-500'
                          : 'bg-[#D4AF37] text-white hover:bg-[#c09d2a]'
                      }`}
                    >
                      {isSubscribed ? 'Abonné' : 'S\'abonner'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Description et produit */}
            <div className="bg-white rounded-xl p-4 md:p-6">
              {/* Tabs */}
              <div className="flex border-b mb-4">
                <button
                  onClick={() => setActiveTab('comments')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === 'comments'
                      ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                      : 'text-gray-600 hover:text-[#D4AF37]'
                  }`}
                >
                  Commentaires ({video.comments})
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === 'info'
                      ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                      : 'text-gray-600 hover:text-[#D4AF37]'
                  }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab('product')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === 'product'
                      ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                      : 'text-gray-600 hover:text-[#D4AF37]'
                  }`}
                >
                  Produit
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'comments' && (
                <div className="space-y-6">
                  {/* Formulaire d'ajout de commentaire */}
                  <form onSubmit={handleCommentSubmit} className="flex gap-3">
                    {(() => {
                      const userPhoto = user?.photo_profil
                        ? (user.photo_profil.startsWith('http') ? user.photo_profil : mediaUrl(user.photo_profil))
                        : null
                      const userInitials = [user?.prenom, user?.nom].filter(Boolean).map(w => w[0]).join('').toUpperCase() || 'V'
                      return userPhoto ? (
                        <img src={userPhoto} alt="Vous" className="w-10 h-10 rounded-full object-cover flex-shrink-0" onError={e => e.target.style.display = 'none'} />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center text-white font-bold flex-shrink-0">{userInitials}</div>
                      )
                    })()}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Ajouter un commentaire..."
                        className="w-full px-4 py-2 border-b-2 border-gray-200 focus:border-[#D4AF37] outline-none transition-colors"
                      />
                      {newComment && (
                        <div className="flex justify-end gap-2 mt-2">
                          <button type="button" onClick={() => setNewComment('')} className="px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-full transition-colors">Annuler</button>
                          <button type="submit" disabled={loadingComment} className="px-4 py-1 text-sm bg-[#D4AF37] text-white rounded-full hover:bg-[#c09d2a] transition-colors disabled:opacity-50">
                            {loadingComment ? '...' : 'Commenter'}
                          </button>
                        </div>
                      )}
                    </div>
                  </form>

                  {/* Liste des commentaires — rendu inline comme ProductDetailPage */}
                  <div className="space-y-4 mt-4">
                    {comments.length === 0 ? (
                      <p className="text-gray-500 text-center py-6">Aucun commentaire. Soyez le premier !</p>
                    ) : comments.map((comment, idx) => {
                      const clientName = comment.client?.name || comment.author || 'Anonyme'
                      const clientPhoto = comment.client?.photo
                        ? (comment.client.photo.startsWith('http') ? comment.client.photo : mediaUrl(comment.client.photo))
                        : null
                      const initials = clientName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                      const commentText = comment.comment_text || comment.text || comment.content || ''
                      const commentDate = comment.created_at
                        ? new Date(comment.created_at).toLocaleDateString('fr-FR')
                        : comment.date || ''
                      return (
                        <div key={comment.id ?? idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                          {clientPhoto ? (
                            <img src={clientPhoto} alt={clientName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-bold">{initials}</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm text-[#001F3F]">{clientName}</span>
                              <span className="text-xs text-gray-500">{commentDate}</span>
                            </div>
                            <p className="text-gray-700 text-sm">{commentText}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'info' && (
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">{video.description}</p>
                  
                  {/* Catégorie et tags */}
                  <div className="space-y-3 border-t pt-4">
                    <div>
                      <span className="text-sm font-semibold text-gray-600">Catégorie : </span>
                      <span className="text-sm text-[#D4AF37]">{video.category}</span>
                    </div>
                    {video.tags && video.tags.length > 0 && (
                      <div>
                        <span className="text-sm font-semibold text-gray-600 block mb-2">Tags :</span>
                        <div className="flex flex-wrap gap-2">
                          {video.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'product' && video.product && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-[#FFF4CC] to-[#FFE8A3] rounded-xl p-6">
                    <h3 className="text-xl font-bold text-[#001F3F] mb-3">{video.product.name}</h3>
                    
                    <div className="flex items-center gap-3 mb-4">
                      {video.product.oldPrice && (
                        <p className="text-gray-500 line-through text-lg">{video.product.oldPrice}</p>
                      )}
                      <p className="text-red-600 font-bold text-2xl">{video.product.price}</p>
                      {video.product.oldPrice && (
                        <span className="px-3 py-1 bg-red-600 text-white text-sm font-bold rounded">
                          -{Math.round((1 - parseFloat(video.product.price.replace(/[^0-9]/g, '')) / parseFloat(video.product.oldPrice.replace(/[^0-9]/g, ''))) * 100)}%
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                          {video.product.condition}
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg">
                          {video.product.stock}
                        </span>
                      </div>
                      <button
                        onClick={() => navigate(`/product/${encodeId(video.product.id)}`)}
                        className="px-6 py-2 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors"
                      >
                        Voir le produit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Colonne droite - Vidéos similaires */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-4 sticky top-4">
              <h2 className="text-lg font-bold text-[#001F3F] mb-4">Vidéos similaires</h2>
              <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                {relatedVideos.map((relatedVideo) => (
                  <button
                    key={relatedVideo.id}
                    onClick={() => {
                      navigate(`/video/${encodeId(relatedVideo.id)}`)
                      window.scrollTo(0, 0)
                    }}
                    className="w-full flex gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors text-left group"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={relatedVideo.thumbnail}
                        alt={relatedVideo.title}
                        className="w-40 h-24 object-cover rounded-lg"
                      />
                      <span className="absolute bottom-1 right-1 px-2 py-0.5 bg-black/80 text-white text-xs rounded">
                        {relatedVideo.duration}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-[#001F3F] line-clamp-2 group-hover:text-[#D4AF37] transition-colors mb-1">
                        {relatedVideo.title}
                      </h3>
                      <p className="text-xs text-gray-600 mb-1">{relatedVideo.author}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{relatedVideo.views.toLocaleString()} vues</span>
                        <span>•</span>
                        <span>{relatedVideo.uploadDate}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de partage */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#001F3F]">Partager cette vidéo</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <button className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-700">Facebook</span>
              </button>

              <button className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-700">WhatsApp</span>
              </button>

              <button className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </div>
                <span className="text-xs text-gray-700">Twitter</span>
              </button>

              <button className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <span className="text-xs text-gray-700">Copier</span>
              </button>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={`${window.location.origin}/video/${encodeId(video.id)}`}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/video/${encodeId(video.id)}`)
                  alert('Lien copié !')
                }}
                className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors"
              >
                Copier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoDetailPage
